import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, Tooltip, Typography } from '@material-ui/core';
import cx from 'classnames';
import { kebabCase } from 'lodash';
import React, { MouseEventHandler, RefObject, useRef } from 'react';
import { translate } from '../../../../../common/intl';
import { Button, IconButton } from '../../../../components/forms/fields-next';
import useSticky from '../../../../hooks/useSticky';
import { SettingsPanelProps } from '../../types';
import useStyles from './styles';

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  title,
  info,
  children,
  containerRef,
  expanded,
  onToggle,
  onAddNew,
  onManageItems,
  isOutOfSync,
  addButtonLabel,
  ...props
}) => {
  const classes = useStyles();
  const [stickyRef, isSticky] = useSticky(containerRef);
  const isAddPending = useRef(false);

  const handleAddNew: MouseEventHandler = (e) => {
    e.stopPropagation();
    if (expanded) {
      onAddNew?.();
      return;
    }
    onToggle();
    isAddPending.current = true;
  };

  const handleManageItems: MouseEventHandler = (e) => {
    e.stopPropagation();
    if (expanded) {
      onManageItems?.();
      return;
    }
    onToggle();
  };

  const handleOnEntered = () => {
    if (isAddPending.current) {
      isAddPending.current = false;
      onAddNew?.();
    }
  };

  return (
    <ExpansionPanel
      {...props}
      expanded={expanded}
      className={classes.root}
      TransitionProps={{
        mountOnEnter: true,
        unmountOnExit: true,
        onEntered: handleOnEntered,
        ...props.TransitionProps,
      }}
    >
      <ExpansionPanelSummary
        ref={stickyRef as RefObject<HTMLDivElement>}
        onClick={onToggle}
        classes={{
          root: cx(classes.summary, { [classes.stickySummary]: isSticky }),
          content: cx(classes.content, { [classes.settingOutOfSync]: isOutOfSync }),
          expandIcon: classes.icon,
        }}
        expandIcon={(
          <IconButton
            kind="secondary"
            size="small"
            iconSize={12}
            className={classes.iconButton}
          >
            <FontAwesomeIcon icon={regular('chevron-right')} />
          </IconButton>
        )}
      >
        <div className={classes.textSection}>
          <Typography variant="h2" className={classes.title}>{title}</Typography>
          {info && <Typography className={classes.info} data-cy={`info-${kebabCase(title)}`}>{info}</Typography>}
        </div>
        {isOutOfSync && (
          <Tooltip title={translate('administration.general.settings.approvalRoles.sync.message.dialog.out.of.sync.with.dam')}>
            <div className={classes.outOfSyncBox}>
              <FontAwesomeIcon className={classes.outOfSyncIcon} icon={regular('rotate-exclamation')} />
            </div>
          </Tooltip>
        )}
        <Box>
          {onManageItems && (
            <Button
              kind="ghost"
              size="small"
              startIcon={<FontAwesomeIcon icon={regular('list-check')} />}
              onClick={handleManageItems}
              data-cy="add-new"
            >
              {translate('common.manage.configuration.items')}
            </Button>
          )}
          {onAddNew && (
            <Button
              kind="ghost"
              size="small"
              startIcon={<FontAwesomeIcon icon={regular('circle-plus')} />}
              onClick={handleAddNew}
              data-cy="add-new"
            >
              {addButtonLabel ?? translate('common.add.new')}
            </Button>
          )}
        </Box>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={cx(classes.details, { [classes.detailsWithStickyTitle]: isSticky })}>
        {children}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

export default SettingsPanel;
